"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { FinanceCore, FinanceData, FinanceTransactions, Txn, YearlyRollup } from "./types";
import { reassembleFinance, type ShardManifestResponse } from "./shards";
import { groupOf } from "./categories";
import { addMonths, round2 } from "./format";
import { usePersistedState } from "./usePersistedState";
import { recordDebugEvent } from "../debug";
import { BUILD_HASH } from "../buildInfo";
import {
  CACHE_NAMES,
  FINANCE_CORE_URL,
  FINANCE_MANIFEST_KEY,
  FINANCE_MANIFEST_URL,
  FINANCE_TXN_URL,
  financeShardUrl,
  financeShardsBatchUrl,
} from "../cache-constants";
import {
  diffManifest,
  fetchManifest,
  handleUnauthorized,
  readCachedJson,
  readCachedTimestamp,
  updateApp,
  writeCachedJson,
  writeCachedTimestamp,
  type AppliedManifest,
} from "../pwa-refresh";

/**
 * Client-side provider for the finance app.
 *
 * Data syncs incrementally (docs/incremental-sync-strategy.md): the store is
 * served as hash-versioned shard bundles, and one manifest request tells this
 * client exactly which bundles its Cache Storage copy is missing or has stale.
 * Cold start and Refresh are the SAME path — "make the cache match the
 * manifest, then reassemble" — so a first load pulls everything once and a
 * refresh after a monthly import pulls only the few hot shards. The app never
 * touches the network on its own: a cached device paints and stays put, and
 * the ONLY network activity is the user tapping Refresh (which checks the
 * manifest and pulls just what changed) — no mount probe, no interval or
 * focus rechecks (see useFinanceSync).
 */

const CACHE = CACHE_NAMES.financeData;
// Legacy monolith cache keys — read once for a seamless migration paint,
// deleted after the first successful shard sync.
const CORE_URL = FINANCE_CORE_URL;
const TXN_URL = FINANCE_TXN_URL;
// Last-refreshed lives in Cache Storage next to the data it describes (the SW
// cache is the single offline store — CLAUDE.md lesson 8). Older builds kept
// it in localStorage; migrated on first read.
const TS_KEY = "/_finance/last-refreshed";
const LEGACY_LS_KEY = "finance.lastRefreshed";

/** Transactions before this date are hidden from every view. The pre-2014
 * ledger holds only the Chase Freedom card and Mint's manual cash entries —
 * no checking account, so no paychecks/bills — which made 2010–2013 cash flow
 * read as spending with no income. Net worth is NOT clamped: it derives from
 * the balance history, which is complete back to 2009. The raw rows stay in
 * the store; widen or drop this constant to surface them again. */
export const LEDGER_DISPLAY_START = "2014-01-01";

/** Drop pre-epoch months/years from the precomputed statements. */
function clampCore(c: FinanceCore): FinanceCore {
  return {
    ...c,
    rollups: {
      monthly: c.rollups.monthly.filter((r) => r.month >= LEDGER_DISPLAY_START.slice(0, 7)),
      yearly: c.rollups.yearly.filter((r) => r.year >= LEDGER_DISPLAY_START.slice(0, 4)),
    },
  };
}

interface FinanceCtx {
  core: FinanceCore | null; // null until the first load lands
  txns: Txn[] | null;
  refreshing: boolean;
  lastRefreshed: number | null;
  /** The store has data this device hasn't pulled yet (Refresh pulls it).
   * With no background probe, the only setter left is the legacy-monolith
   * migration paint — a nudge to Refresh once onto the shard layout. */
  updateAvailable: boolean;
  /** A newer app build is deployed (Update reloads onto it). Only learned
   * from the manifest a manual Refresh fetches — nothing probes on its own. */
  appUpdateAvailable: boolean;
  refresh: () => Promise<void>;
  update: () => void;
  /** Per-tag include/exclude policy (default: reimbursable excluded, rest in). */
  tagPolicy: TagPolicy;
  setTagPolicy: (v: TagPolicy | ((prev: TagPolicy) => TagPolicy)) => void;
}

const FinanceContext = createContext<FinanceCtx | null>(null);

/** Server-side cap on ids per ?type=shards request (route validates it). A
 * cold start (~63 bundles) fits one request; chunking only kicks in if the
 * shard count ever outgrows the cap. */
const SHARD_BATCH_LIMIT = 100;

/**
 * Fetch a set of shards in ONE ?type=shards request and cache each at its
 * per-shard URL key — offline reads, cache healing, and reassembly stay
 * per-shard and unchanged. Ids the server doesn't return land in `failed`.
 * 401 → sign in (unless offline — never bounce a user with cached data to
 * Google login).
 */
async function fetchShardBatch(ids: string[], failed: Set<string>): Promise<void> {
  const t0 = performance.now();
  const url = financeShardsBatchUrl(ids);
  const res = await fetch(url, { cache: "reload" });
  if (res.status === 401) {
    handleUnauthorized(window.location.pathname);
    for (const id of ids) failed.add(id);
    return;
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  // Wire size when Content-Length is present (the API gzips, so that's the
  // compressed size actually downloaded), decoded size otherwise. Note
  // writeCachedJson re-serializes the parsed payload rather than storing
  // res.clone() — a stored Response claiming "Content-Encoding: gzip" over an
  // already-decoded body is exactly the kind of thing Safari trips on.
  const wireBytes = parseInt(res.headers.get("content-length") ?? "", 10);
  const body = (await res.json()) as { shards?: Record<string, unknown> };
  const shards = body.shards ?? {};
  for (const id of ids) {
    const payload = shards[id];
    if (payload === undefined) failed.add(id);
    else await writeCachedJson(CACHE, financeShardUrl(id), payload);
  }
  recordDebugEvent({
    kind: "finance-data",
    // The real URL carries every id (~1 KB on a cold start) — log the bare
    // endpoint and put the count in detail instead.
    url: "/api/finance?type=shards",
    source: `network→cache:${CACHE}`,
    durationMs: Math.round(performance.now() - t0),
    status: res.status,
    bytes: Number.isFinite(wireBytes) ? wireBytes : JSON.stringify(body).length,
    detail: `${ids.length} shard(s) in one batch`,
  });
}

/** ?type=transactions is a plain date-desc Txn[]; older cached responses used
 * the two-array shape (merge + sort those on read). Pre-epoch rows are
 * clamped out here (see LEDGER_DISPLAY_START). */
function parseTxns(t: FinanceTransactions): Txn[] {
  const rows = Array.isArray(t)
    ? t
    : [...t.transactionsMint, ...t.transactionsEmpower].sort((a, b) => (a.date < b.date ? 1 : -1));
  return rows.filter((tx) => tx.date >= LEDGER_DISPLAY_START);
}

/** Read last-refreshed from Cache Storage, migrating the legacy localStorage key. */
async function readLastRefreshed(): Promise<number | null> {
  const ts = await readCachedTimestamp(CACHE, TS_KEY);
  if (ts) return ts.getTime();
  try {
    const legacy = Number(localStorage.getItem(LEGACY_LS_KEY)) || null;
    if (legacy) {
      await writeCachedTimestamp(CACHE, TS_KEY, new Date(legacy));
      localStorage.removeItem(LEGACY_LS_KEY);
    }
    return legacy;
  } catch {
    return null;
  }
}

/** Read every bundle the applied manifest lists out of Cache Storage.
 * Null when any is missing — the caller heals by syncing to the manifest. */
async function readShardMap(applied: AppliedManifest): Promise<Map<string, unknown> | null> {
  const map = new Map<string, unknown>();
  for (const id of Object.keys(applied.bundles)) {
    const payload = await readCachedJson<unknown>(CACHE, financeShardUrl(id));
    if (payload === null) return null;
    map.set(id, payload);
  }
  return map;
}

export function FinanceDataProvider({ children }: { children: ReactNode }) {
  const [core, setCore] = useState<FinanceCore | null>(null);
  const [txns, setTxns] = useState<Txn[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [appUpdateAvailable, setAppUpdateAvailable] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null);
  const [tagPolicy, setTagPolicy] = usePersistedState<TagPolicy>("fin.tagPolicy", EMPTY_TAG_POLICY);
  const started = useRef(false);

  const paint = useCallback((c: FinanceCore, rows: Txn[]) => {
    setCore(clampCore(c));
    setTxns(rows.filter((t) => t.date >= LEDGER_DISPLAY_START));
  }, []);

  /**
   * The one sync path (cold start AND Refresh): make the cached shard set
   * match the server manifest, then reassemble and repaint. Only bundles
   * whose hash differs from what this device has applied (plus any missing
   * from the cache) are fetched — an up-to-date refresh transfers just the
   * manifest. The applied manifest records per-bundle what the cache actually
   * holds, so an interrupted sync never re-downloads what already landed and
   * never claims freshness it doesn't have.
   */
  const syncToManifest = useCallback(async () => {
    const fresh = await fetchManifest<ShardManifestResponse>(FINANCE_MANIFEST_URL);
    if (fresh.build && BUILD_HASH && fresh.build !== BUILD_HASH) setAppUpdateAvailable(true);

    const applied = await readCachedJson<AppliedManifest>(CACHE, FINANCE_MANIFEST_KEY);
    const cache = await caches.open(CACHE);
    const ids = Object.keys(fresh.bundles ?? {}); // defensive: deploy-skew servers
    if (!ids.length) throw new Error("empty manifest");
    const needed = new Set(diffManifest(applied, fresh));
    // Heal: a bundle whose hash matches but whose cache entry is gone
    // (eviction, interrupted write) must be re-fetched too.
    for (const id of ids) {
      if (!needed.has(id) && !(await cache.match(financeShardUrl(id)))) needed.add(id);
    }

    // One request for the whole changed set — the server pulls the shards
    // concurrently (Firestore: a single getAll RPC) instead of this client
    // firing a request per shard.
    const failed = new Set<string>();
    const wanted = [...needed];
    for (let i = 0; i < wanted.length; i += SHARD_BATCH_LIMIT) {
      const chunk = wanted.slice(i, i + SHARD_BATCH_LIMIT);
      try {
        await fetchShardBatch(chunk, failed);
      } catch {
        for (const id of chunk) failed.add(id);
      }
    }

    // Drop shards the server no longer lists (e.g. hot months folded into a
    // frozen year shard at year end).
    if (applied) {
      for (const id of Object.keys(applied.bundles)) {
        if (!(id in fresh.bundles)) await cache.delete(financeShardUrl(id));
      }
    }

    // Record per-bundle exactly what the cache now holds.
    const bundles: Record<string, string> = {};
    for (const id of ids) {
      bundles[id] = failed.has(id) ? applied?.bundles[id] ?? "" : fresh.bundles[id];
    }
    await writeCachedJson(CACHE, FINANCE_MANIFEST_KEY, {
      schema: fresh.schema,
      generatedAt: failed.size ? applied?.generatedAt ?? "" : fresh.generatedAt,
      bundles,
    } satisfies AppliedManifest);

    if (failed.size) throw new Error(`${failed.size} bundle(s) failed to sync`);

    const map = await readShardMap({ schema: fresh.schema, generatedAt: fresh.generatedAt, bundles });
    const out = map && reassembleFinance(map);
    if (!out) throw new Error("shard reassembly failed");
    paint(out.core, out.txns);

    const now = new Date();
    setLastRefreshed(now.getTime());
    setUpdateAvailable(false);
    await writeCachedTimestamp(CACHE, TS_KEY, now);
    // Legacy monolith entries are dead weight once shards are in place.
    await cache.delete(CORE_URL);
    await cache.delete(TXN_URL);
  }, [paint]);

  // Refresh button — incremental data pull, in place, no reload. App updates
  // are a separate explicit action (see `update` below); a data refresh never
  // clears caches or touches the service worker.
  const doRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await syncToManifest();
    } catch (e) {
      if ((e as { status?: number }).status === 401) {
        handleUnauthorized(window.location.pathname);
        return;
      }
      // With data already on screen a failed refresh keeps the stale copy
      // (and the update nudge); only a first load with nothing to show
      // surfaces the full-page error.
      recordDebugEvent({ kind: "finance-data", url: FINANCE_MANIFEST_URL, source: "error", detail: `refresh failed — ${String(e)}` });
      setCore((current) => {
        if (!current) setError(String(e));
        return current;
      });
    } finally {
      setRefreshing(false);
    }
  }, [syncToManifest]);

  // Explicit app update: SW update + shell-cache drop + reload (data caches
  // untouched). The spinner stays up — the page is navigating away.
  const doUpdate = useCallback(() => {
    setRefreshing(true);
    void updateApp();
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      try {
        // Cache-first: paint immediately from the applied shard set.
        const applied = await readCachedJson<AppliedManifest>(CACHE, FINANCE_MANIFEST_KEY);
        if (applied) {
          const map = await readShardMap(applied);
          const out = map && reassembleFinance(map);
          if (out) {
            // Cached device: paint and stay put — zero network until the
            // user taps Refresh.
            paint(out.core, out.txns);
            setLastRefreshed(await readLastRefreshed());
            return;
          }
          // Cache entries went missing under the manifest — fall through and
          // sync (only the missing bundles will actually be fetched).
        }

        // Migration: a device that cached the old monolith paints from it
        // instantly; the pending shard pull is flagged as an update so the
        // user's next Refresh migrates without an auto-refetch on open.
        const legacyCore = await readCachedJson<FinanceCore>(CACHE, CORE_URL);
        if (!applied && legacyCore) {
          const legacyTxns = await readCachedJson<FinanceTransactions>(CACHE, TXN_URL);
          setCore(clampCore(legacyCore));
          if (legacyTxns) setTxns(parseTxns(legacyTxns));
          setLastRefreshed(await readLastRefreshed());
          setUpdateAvailable(true);
          return;
        }

        // First-ever load (or incomplete cache): sync to the manifest.
        await syncToManifest();
      } catch (e) {
        // A 401 here is the "visited /finance signed out" case: go to the
        // login screen (skeletons stay up until the navigation lands) rather
        // than render "Failed to load data: 401" with no way forward.
        if ((e as { status?: number }).status === 401) {
          handleUnauthorized(window.location.pathname);
          return;
        }
        setError(String(e));
      }
    })();
  }, [syncToManifest, paint]);

  const value = useMemo<FinanceCtx>(
    () => ({
      core,
      txns,
      refreshing,
      lastRefreshed,
      updateAvailable,
      appUpdateAvailable,
      refresh: doRefresh,
      update: doUpdate,
      tagPolicy,
      setTagPolicy,
    }),
    [core, txns, refreshing, lastRefreshed, updateAvailable, appUpdateAvailable, doRefresh, doUpdate, tagPolicy, setTagPolicy],
  );

  if (error)
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-sm text-ink-2">
        Failed to load data: {error}
      </div>
    );
  // Never block on data: the chrome + per-widget skeletons render immediately
  // (FinanceChrome gates its content on useFinanceReady()).
  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>;
}

function useCtx(): FinanceCtx {
  const ctx = useContext(FinanceContext);
  if (!ctx) throw new Error("useFinance outside FinanceDataProvider");
  return ctx;
}

export function useFinance(): FinanceData {
  const { core, txns } = useCtx();
  if (!core) throw new Error("useFinance() before data ready — gate render on useFinanceReady()");
  return { ...core, txns };
}

/** True once the core payload has loaded — chrome uses this to swap the
 * content area between skeletons and the real page. */
export function useFinanceReady(): boolean {
  return !!useCtx().core;
}

/** Refresh controls for the header bar (Refresh/Update button, flags). */
export function useFinanceSync() {
  const { refreshing, lastRefreshed, updateAvailable, appUpdateAvailable, refresh, update } = useCtx();
  return { refreshing, lastRefreshed, updateAvailable, appUpdateAvailable, refresh, update };
}

/* --------------------------------------------------------------------------
 * Derivations
 * ------------------------------------------------------------------------ */

/**
 * Tag-based budgeting policy. Each Empower tag is either "include" (counts
 * toward spending/income) or "exclude" (hidden from budgeting). The DEFAULT —
 * used for any tag with no explicit choice — matches Empower: a "reimbursable"
 * tag is excluded, every other tag is included. The user overrides per tag via
 * the tag menu (see useTagPolicy).
 */
export type TagPolicy = Record<string, "include" | "exclude">;
export const EMPTY_TAG_POLICY: TagPolicy = {};

/** A "reimbursable"-tagged txn (a matching expense you expect to be paid back)
 * is excluded from budgeting by DEFAULT. Kept for the default-policy math and
 * as the one tag with a non-include default. */
export function isReimbursable(t: Txn): boolean {
  return !!t.tags?.some((tag) => /reimburs/i.test(tag));
}

/** The default (no explicit choice) exclusion for a tag: reimbursable and
 * "Excluded" (the tag that replaced Mint's hard exclude flag, Jul 2026) →
 * out, everything else → in. MUST stay in sync with isReimbursable in
 * aggregate.ts — the precomputed rollups skip exactly these by default. */
function tagExcludedByDefault(tag: string): boolean {
  return /reimburs/i.test(tag) || /^excluded$/i.test(tag);
}

/** Effective exclusion for a tag under a policy: the explicit choice if set,
 * else the default. */
export function tagExcluded(tag: string, policy: TagPolicy): boolean {
  const p = policy[tag];
  return p ? p === "exclude" : tagExcludedByDefault(tag);
}

/** A txn is hidden from budgeting when ANY of its tags is excluded under the
 * current policy. */
export function isExcludedByTag(t: Txn, policy: TagPolicy = EMPTY_TAG_POLICY): boolean {
  return !!t.tags?.some((tag) => tagExcluded(tag, policy));
}

/** True if this transaction counts toward income/spending reports —
 * reportable rollup, not excluded, in a budgetable account, and not hidden by
 * an excluded tag under `policy` (default policy hides only reimbursable). */
export function isReportable(t: Txn, policy: TagPolicy = EMPTY_TAG_POLICY): boolean {
  if (isExcludedByTag(t, policy)) return false;
  return t.budgetable && (t.rollup === "Expense" || t.rollup === "Income");
}

export function isSpending(t: Txn, policy: TagPolicy = EMPTY_TAG_POLICY): boolean {
  if (isExcludedByTag(t, policy)) return false;
  return t.budgetable && t.rollup === "Expense";
}

export function isIncome(t: Txn, policy: TagPolicy = EMPTY_TAG_POLICY): boolean {
  if (isExcludedByTag(t, policy)) return false;
  return t.budgetable && t.rollup === "Income";
}

/** True when a policy is EQUIVALENT to the default (reimbursable out,
 * everything else in) — explicit choices that merely restate the default
 * don't count as a deviation. Drives the "Reset filters" chip. */
export function tagPolicyIsDefault(policy: TagPolicy): boolean {
  return Object.entries(policy).every(([tag, v]) => (v === "exclude") === tagExcludedByDefault(tag));
}

/** The persisted per-tag include/exclude policy plus the set of tags present in
 * the data. `setTag(tag, include)` flips one tag; the choice applies everywhere
 * budgeting is computed (Trends, Cash Flow, Overview, Transactions totals).
 * `reset()` drops every explicit choice, restoring the default policy. */
export function useTagPolicy(): {
  policy: TagPolicy;
  setTag: (tag: string, include: boolean) => void;
  tags: string[];
  reset: () => void;
} {
  const { tagPolicy, setTagPolicy, txns } = useCtx();
  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const t of txns ?? []) for (const tag of t.tags ?? []) set.add(tag);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [txns]);
  const setTag = useCallback(
    (tag: string, include: boolean) =>
      setTagPolicy((prev) => ({ ...prev, [tag]: include ? "include" : "exclude" })),
    [setTagPolicy],
  );
  const reset = useCallback(() => setTagPolicy(EMPTY_TAG_POLICY), [setTagPolicy]);
  return { policy: tagPolicy, setTag, tags, reset };
}

export interface MonthAgg {
  month: string;
  income: number;
  spending: number; // positive number
  net: number;
  /** spending by group, positive numbers (folded from byCategory) */
  byGroup: Record<string, number>;
  /** spending by category, positive numbers, largest first */
  byCategory: Record<string, number>;
}

function foldByGroup(byCategory: Record<string, number>): Record<string, number> {
  const byGroup: Record<string, number> = {};
  for (const [category, spent] of Object.entries(byCategory)) {
    const g = groupOf(category);
    byGroup[g] = (byGroup[g] ?? 0) + spent;
  }
  return byGroup;
}

/**
 * Adjust a precomputed rollup bucket for the tag policy. The base rollups
 * (rollups.json) already apply the DEFAULT policy — a row is in them iff it has
 * no reimbursable tag. For each budgetable tagged row whose EFFECTIVE inclusion
 * differs from that default, add it back (newly included) or subtract it out
 * (newly excluded). No-ops entirely when the policy is default, so this runs
 * unconditionally. Mutates a shallow-cloned map.
 */
function applyTagDeltas<T extends { income: number; spending: number; byCategory: Record<string, number> }>(
  base: Map<string, T>,
  txns: Txn[],
  keyOf: (t: Txn) => string,
  make: (key: string) => T,
  policy: TagPolicy,
): T[] {
  for (const t of txns) {
    if (!t.tags?.length || !t.budgetable) continue;
    if (t.rollup !== "Expense" && t.rollup !== "Income") continue;
    const inBase = !t.tags.some(tagExcludedByDefault); // base = default policy
    const inTarget = !isExcludedByTag(t, policy);
    if (inBase === inTarget) continue;
    const sign = inTarget ? 1 : -1; // add if newly included, subtract if newly excluded
    const key = keyOf(t);
    let b = base.get(key);
    if (!b) base.set(key, (b = make(key)));
    if (t.rollup === "Income") b.income = round2(b.income + sign * t.amount);
    else {
      const s = sign * -t.amount;
      b.spending = round2(b.spending + s);
      const c = round2((b.byCategory[t.category] ?? 0) + s);
      if (Math.abs(c) < 0.005) delete b.byCategory[t.category];
      else b.byCategory[t.category] = c;
    }
  }
  return [...base.values()];
}

/**
 * Monthly statements, precomputed at import time (rollups.json, default policy).
 * Tag-policy overrides are applied here from the raw transactions (add newly
 * included tags, subtract newly excluded ones).
 */
export function useMonthlyAgg(): MonthAgg[] {
  const { rollups, txns } = useFinance();
  const { tagPolicy } = useCtx();
  return useMemo(() => {
    const base = new Map(
      rollups.monthly.map((r) => [
        r.month,
        { month: r.month, income: r.income, spending: r.spending, byCategory: { ...r.byCategory } },
      ]),
    );
    if (txns)
      applyTagDeltas(base, txns, (t) => t.date.slice(0, 7), (month) => ({ month, income: 0, spending: 0, byCategory: {} }), tagPolicy);
    return [...base.values()]
      .sort((a, b) => (a.month < b.month ? -1 : 1))
      .map((m) => ({
        month: m.month,
        income: m.income,
        spending: m.spending,
        net: round2(m.income - m.spending),
        byGroup: foldByGroup(m.byCategory),
        byCategory: m.byCategory,
      }));
  }, [rollups, txns, tagPolicy]);
}

/** Yearly statements, precomputed at import time (tag-policy deltas applied). */
export function useYearlyAgg(): (YearlyRollup & { byGroup: Record<string, number> })[] {
  const { rollups, txns } = useFinance();
  const { tagPolicy } = useCtx();
  return useMemo(() => {
    const base = new Map(
      rollups.yearly.map((r) => [
        r.year,
        { year: r.year, income: r.income, spending: r.spending, byCategory: { ...r.byCategory } },
      ]),
    );
    if (txns)
      applyTagDeltas(base, txns, (t) => t.date.slice(0, 4), (year) => ({ year, income: 0, spending: 0, byCategory: {} }), tagPolicy);
    return [...base.values()]
      .sort((a, b) => (a.year < b.year ? -1 : 1))
      .map((y) => ({
        year: y.year,
        income: y.income,
        spending: y.spending,
        net: round2(y.income - y.spending),
        byCategory: y.byCategory,
        byGroup: foldByGroup(y.byCategory),
      }));
  }, [rollups, txns, tagPolicy]);
}

/** The latest month present in the balance history. */
export function latestMonth(months: string[]): string {
  return months[months.length - 1];
}

/** The monthly aggregates covering [startM, endM] (YYYY-MM, inclusive), with
 * months that have no imported transactions filled in as empty rows — so a
 * range anchored to today doesn't snap back to older data. */
export function monthlyWindow(monthly: MonthAgg[], startM: string, endM: string): MonthAgg[] {
  const byMonth = new Map(monthly.map((m) => [m.month, m]));
  const out: MonthAgg[] = [];
  for (let m = startM; m <= endM; m = addMonths(m, 1)) {
    out.push(byMonth.get(m) ?? { month: m, income: 0, spending: 0, net: 0, byGroup: {}, byCategory: {} });
  }
  return out;
}
